/**
 * /setup/wizard — a company owner's initial setup, resumable. Steps are saved on
 * the organization (setup.completedSteps); the required ones gate "Finish".
 * Deep link with ?step=agreements.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Container, Snackbar, Step, StepButton, Stepper, Typography } from '@mui/material';
import SideMenu from '../../components/SideMenu';
import { useUser } from '../../context/UserContext';
import { useOrg } from '../../context/OrgContext';
import { settingsAccess } from '../../utils/settingsAccess';
import { getMe, markStep as apiMarkStep, completeSetup, errorMessage } from './wizardApi';
import { STEP_COMPONENTS, STEP_META, StepFinish } from './steps';
import { usePageTour } from '../../tours/usePageTour';
import { queueTour } from '../../tours/TourProvider';

export default function Wizard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { currentUser, isLoading: userLoading } = useUser();
  const { refresh } = useOrg();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState(null);
  const [completing, setCompleting] = useState(false);

  const allowed = useMemo(() => (currentUser ? settingsAccess(currentUser).orgOwner : false), [currentUser]);
  // The wizard's own tour, once the company has loaded
  usePageTour('setup-wizard', allowed && !!org);

  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login', { replace: true });
  }, [navigate]);

  const reload = useCallback(async () => {
    const data = await getMe();
    setOrg(data);
    return data;
  }, []);

  useEffect(() => {
    if (!allowed) return;
    reload().catch((err) => setError(errorMessage(err, 'Could not load your company')));
  }, [allowed, reload]);

  const notify = useCallback((message, severity = 'info') => setSnack({ message, severity }), []);

  const completed = useMemo(() => new Set(org?.setup?.completedSteps || []), [org]);
  const firstOpen = STEP_META.find((s) => s.key !== 'finish' && !completed.has(s.key))?.key || 'finish';
  const activeKey = STEP_META.some((s) => s.key === params.get('step')) ? params.get('step') : firstOpen;
  const activeIndex = STEP_META.findIndex((s) => s.key === activeKey);

  const goTo = (key) => { setParams({ step: key }); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const markDone = useCallback(async (step) => {
    const r = await apiMarkStep(step);
    setOrg((o) => (o ? { ...o, setup: { ...o.setup, completedSteps: r.completedSteps, completedAt: r.completedAt } } : o));
    const idx = STEP_META.findIndex((s) => s.key === step);
    const next = STEP_META[idx + 1]?.key || 'finish';
    setParams({ step: next });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setParams]);

  const missing = useMemo(() => {
    const req = STEP_META.filter((s) => s.required).map((s) => s.key).filter((k) => !completed.has(k));
    if (org && !org.branding?.logoUrl && org.kind === 'partner' && !req.includes('branding')) req.push('branding');
    return req;
  }, [completed, org]);

  const onComplete = async () => {
    setCompleting(true);
    try {
      const data = await completeSetup();
      setOrg(data);
      refresh();
      notify('Setup complete. Your company is active.', 'success');
      // The welcome tour starts on the Dashboard and walks the owner into the Owner Console
      queueTour('welcome');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      notify(errorMessage(err, 'Could not finish setup'), 'error');
    } finally {
      setCompleting(false);
    }
  };

  if (userLoading || (!currentUser && localStorage.getItem('token'))) {
    return <><SideMenu /><Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box></>;
  }
  if (!allowed) {
    return <><SideMenu /><Container maxWidth="sm" sx={{ mt: 6 }}><Alert severity="warning">Company setup is for owners only.</Alert></Container></>;
  }

  const Active = STEP_COMPONENTS[activeKey];

  return (
    <>
      <SideMenu />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Box>
            <Typography variant="h4">Company setup</Typography>
            <Typography variant="body2" color="text.secondary">
              {org?.setup?.completedAt ? 'Setup is complete. Change anything here at any time.' : 'Work through the steps in any order. Required steps are marked; the rest can wait.'}
            </Typography>
          </Box>
          {org && <Chip label={org.setup?.completedAt ? 'Active' : `${completed.size - (completed.has('claimed') ? 1 : 0)} of ${STEP_META.length - 1} steps done`} color={org.setup?.completedAt ? 'success' : 'default'} />}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stepper nonLinear activeStep={activeIndex} alternativeLabel sx={{ mb: 3, overflowX: 'auto' }} data-tour="wizard-stepper">
          {STEP_META.map((s) => (
            <Step key={s.key} completed={s.key === 'finish' ? !!org?.setup?.completedAt : completed.has(s.key)}>
              <StepButton color="inherit" onClick={() => goTo(s.key)} data-tour={`wizard-step-${s.key}`} optional={s.required ? <Typography variant="caption" color="primary">Required</Typography> : undefined}>
                {s.label}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <Box data-tour="wizard-content">
        {!org ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : activeKey === 'finish' ? (
          <StepFinish org={org} completing={completing} onComplete={onComplete} missing={missing} />
        ) : (
          <Active org={org} reload={reload} notify={notify} markDone={markDone} />
        )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }} data-tour="wizard-nav">
          <Button disabled={activeIndex <= 0} onClick={() => goTo(STEP_META[activeIndex - 1].key)}>Back</Button>
          {activeKey !== 'finish' && <Button onClick={() => goTo(STEP_META[activeIndex + 1].key)}>Skip for now</Button>}
        </Box>
      </Container>
      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
